import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { BASE_URL } from '../../redux/actions/ip';

// Date-range presets exposed in the picker.
export const DATE_PRESETS = [
  { id: 'last30',   labelKey: 'claimsAnalytics.last30',   days: 30 },
  { id: 'last90',   labelKey: 'claimsAnalytics.last90',   days: 90 },
  { id: 'last6m',   labelKey: 'claimsAnalytics.last6m',   days: 180 },
  { id: 'last12m',  labelKey: 'claimsAnalytics.last12m',  days: 365 },
  { id: 'ytd',      labelKey: 'claimsAnalytics.ytd' },
  { id: 'all',      labelKey: 'claimsAnalytics.all' },
  { id: 'custom',   labelKey: 'claimsAnalytics.custom' },
];

function rangeForPreset(preset, customFrom, customTo) {
  const today = new Date();
  if (preset === 'all') return { dateFrom: null, dateTo: null };
  if (preset === 'ytd') {
    return { dateFrom: new Date(today.getFullYear(), 0, 1).toISOString(), dateTo: today.toISOString() };
  }
  if (preset === 'custom') {
    return {
      dateFrom: customFrom ? new Date(customFrom).toISOString() : null,
      dateTo:   customTo   ? new Date(customTo).toISOString()   : null,
    };
  }
  const cfg = DATE_PRESETS.find(p => p.id === preset);
  if (cfg && cfg.days) {
    const from = new Date(today);
    from.setDate(from.getDate() - cfg.days);
    return { dateFrom: from.toISOString(), dateTo: today.toISOString() };
  }
  return { dateFrom: null, dateTo: null };
}

const ANALYTICS_BASE = `${BASE_URL}/claims/analytics`;

// Values the backend emits when it could not classify a claim on a
// dimension. A bucket carrying one of these is a placeholder, not a finding.
const UNCLASSIFIED = new Set(['Unknown', 'Unspecified', 'unknown', 'unspecified', 'No Broker', '']);

/**
 * Does this dimension actually classify anything?
 *
 * A tenant that does not capture date-of-birth gets a By Age Group table
 * whose only row is "Unclassified" holding every claim; the same happens to
 * By Canton and By Specialization. The table is not empty — which is why it
 * was displayed — but it carries no information, and presenting it as
 * analysis invites conclusions from a field nobody filled in.
 *
 * A dimension is useful when at least one bucket names a real value.
 */
export function dimensionHasSignal(rows, dimensionKey) {
  if (!Array.isArray(rows) || rows.length === 0) return false;
  return rows.some(r => {
    const v = r?.[dimensionKey];
    return v != null && String(v).trim() !== '' && !UNCLASSIFIED.has(String(v).trim());
  });
}

export const TABS = [
  { id: 'age',           labelKey: 'claimsAnalytics.tabAge',           dimensionKey: 'ageBand',        endpoint: '/by-age-group' },
  { id: 'specialization',labelKey: 'claimsAnalytics.tabSpecialization', dimensionKey: 'specialization', endpoint: '/by-specialization' },
  { id: 'product',       labelKey: 'claimsAnalytics.tabProduct',       dimensionKey: 'productLabel',   endpoint: '/by-product' },
  { id: 'canton',        labelKey: 'claimsAnalytics.tabCanton',        dimensionKey: 'canton',         endpoint: '/by-canton' },
  { id: 'broker',        labelKey: 'claimsAnalytics.tabBroker',        dimensionKey: 'broker',         endpoint: '/by-broker' },
];

export default function useClaimsAnalyticsFunctions() {
  // M-23: default to 'all' so the analytics summary count matches the
  // unfiltered claims list. Previously 'last12m' silently excluded any
  // claim whose lossDate was older than a year (Anjani reported 26 vs 27
  // on AROMED staging). Users can still narrow via the preset picker.
  const [preset, setPreset]               = useState('all');
  const [customFrom, setCustomFrom]       = useState('');
  const [customTo, setCustomTo]           = useState('');
  const [activeTab, setActiveTab]         = useState('age');
  const [sortBy, setSortBy]               = useState({ column: 'claimCount', direction: 'desc' });
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);

  const [summary, setSummary]                       = useState(null);
  const [productBreakdown, setProductBreakdown]     = useState([]);
  const [reserveDevelopment, setReserveDevelopment] = useState([]);
  const [tableData, setTableData]                   = useState({});

  const range = useMemo(() => rangeForPreset(preset, customFrom, customTo), [preset, customFrom, customTo]);

  const buildParams = useCallback(() => {
    const params = {};
    if (range.dateFrom) params.dateFrom = range.dateFrom;
    if (range.dateTo)   params.dateTo   = range.dateTo;
    return params;
  }, [range]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = buildParams();
      const requests = [
        axios.get(`${ANALYTICS_BASE}/summary`, { params, withCredentials: true }),
        axios.get(`${ANALYTICS_BASE}/by-product`, { params, withCredentials: true }),
        axios.get(`${ANALYTICS_BASE}/reserve-development`, { params, withCredentials: true }),
        ...TABS.map(t => axios.get(`${ANALYTICS_BASE}${t.endpoint}`, { params, withCredentials: true })),
      ];
      const results = await Promise.all(requests);
      const [summaryRes, productRes, reserveRes, ...tabResults] = results;
      setSummary(summaryRes.data?.data || null);
      setProductBreakdown(productRes.data?.data || []);
      setReserveDevelopment(reserveRes.data?.data || []);
      const td = {};
      TABS.forEach((tab, idx) => { td[tab.id] = tabResults[idx].data?.data || []; });
      setTableData(td);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSort = column => {
    setSortBy(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  // Tabs are suppressed only once the data has actually loaded — an empty
  // tableData during the initial fetch must not hide the whole tab bar.
  // If NOTHING classifies, the tabs stay visible and each table explains
  // itself, so the screen never collapses to nothing.
  // While loading this returned EVERY tab, so a tenant briefly saw the full
  // hardcoded dimension list — "By Age Group", "By Specialization",
  // "By Canton" — before it collapsed to the two dimensions it actually has.
  // Those are clinical and Swiss labels; a Spanish travel-assistance tenant
  // advertising them, even for a beat, is the loading state asserting a
  // capability nobody configured. Resolve to nothing and let the tab bar
  // render neutral placeholders instead.
  const usefulTabIds = useMemo(() => {
    if (loading) return [];
    const useful = TABS.filter(t => dimensionHasSignal(tableData[t.id], t.dimensionKey));
    return useful.length ? useful.map(t => t.id) : TABS.map(t => t.id);
  }, [tableData, loading]);

  const visibleTabs = useMemo(
    () => TABS.filter(t => usefulTabIds.includes(t.id)),
    [usefulTabIds],
  );

  // Keep the selection valid when the active tab is suppressed.
  useEffect(() => {
    if (!usefulTabIds.includes(activeTab) && usefulTabIds.length) {
      setActiveTab(usefulTabIds[0]);
    }
  }, [usefulTabIds, activeTab]);

  const activeTabHasSignal = useMemo(() => {
    if (loading) return true;
    const tab = TABS.find(t => t.id === activeTab);
    return tab ? dimensionHasSignal(tableData[activeTab], tab.dimensionKey) : true;
  }, [tableData, activeTab, loading]);

  const sortedTableRows = useMemo(() => {
    const rows = tableData[activeTab] || [];
    const dir = sortBy.direction === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
      const av = a[sortBy.column];
      const bv = b[sortBy.column];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [tableData, activeTab, sortBy]);

  const exportCsv = useCallback(() => {
    const rows = sortedTableRows;
    if (!rows.length) return;
    const tab = TABS.find(t => t.id === activeTab);
    const dimensionKey = tab.dimensionKey;
    const headers = [dimensionKey, 'claimCount', 'totalReserve', 'totalPaid', 'lossRatio', 'averageDuration'];
    const csvLines = [headers.join(',')];
    for (const r of rows) {
      const line = headers.map(h => {
        const v = r[h];
        if (v == null) return '';
        return typeof v === 'string' && v.includes(',') ? `"${v.replace(/"/g, '""')}"` : v;
      }).join(',');
      csvLines.push(line);
    }
    const blob = new Blob([csvLines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `claims-analytics-${tab.id}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [sortedTableRows, activeTab]);

  return {
    preset, setPreset,
    customFrom, setCustomFrom,
    customTo, setCustomTo,
    activeTab, setActiveTab,
    sortBy, handleSort,
    loading, error,
    summary,
    productBreakdown,
    reserveDevelopment,
    tableData,
    visibleTabs,
    activeTabHasSignal,
    sortedTableRows,
    refresh: fetchAll,
    exportCsv,
  };
}
